import React, { useState, useEffect, useRef } from 'react';
import './Templafy.css';
import Workspace from './Workspace';
import FullScreenLoadingView from './FullScreenLoadingView';
import FullScreenResultsView from './FullScreenResultsView';

const WORDS = ['AI agents', 'true productivity', 'real automation', 'peak productivity', 'smooth workflow', 'PowerPoint magic', 'Templafy'];

const Templafy = () => {
  const [currentWordIndex, setCurrentWordIndex] = useState(0);
  const [displayText, setDisplayText] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);
  const [hoveredCard, setHoveredCard] = useState(null);
  const [marketingAnimation, setMarketingAnimation] = useState(null);
  const [selectedCard, setSelectedCard] = useState(null);
  const [isExpanded, setIsExpanded] = useState(false);
  const [cardPosition, setCardPosition] = useState(null);
  const [isReturning, setIsReturning] = useState(false);
  const [hoverDisabled, setHoverDisabled] = useState(false);
  const [email, setEmail] = useState('');
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [showWorkspace, setShowWorkspace] = useState(false);
  const [showCardsContent, setShowCardsContent] = useState(true);
  const [currentView, setCurrentView] = useState('landing'); // 'landing', 'workspace', 'loading', 'results'
  const [pageTransition, setPageTransition] = useState(false); // blank screen between pages
  const [btnBottomOffset, setBtnBottomOffset] = useState(24);
  const [sectionRect, setSectionRect] = useState(null);
  const cardsContainerRef = useRef(null);
  const cardsSectionRef = useRef(null);
  const lottieRef = useRef(null);
  const cardRefs = useRef({});

  // Keep "Get Started" button visible: 24px from card bottom or section bottom, whichever is higher on screen
  useEffect(() => {
    const update = () => {
      const card = cardRefs.current[0];
      const section = cardsSectionRef.current;
      if (!card || !section) return;

      const cardRect = card.getBoundingClientRect();
      const sectionRect = section.getBoundingClientRect();

      // The lowest visible bottom edge (whichever is higher on screen = smaller value)
      const visibleBottom = Math.min(cardRect.bottom, sectionRect.bottom);
      // How far from the card's top is that visible bottom
      const availableHeight = visibleBottom - cardRect.top;
      // Button bottom offset within the card: card height - available height + 24px padding
      const offset = Math.max(24, cardRect.height - availableHeight + 24);
      setBtnBottomOffset(offset);
    };

    update();
    window.addEventListener('resize', update);
    return () => window.removeEventListener('resize', update);
  }, [selectedCard, isExpanded]);

  // Load Lottie animation
  useEffect(() => {
    fetch(`${process.env.PUBLIC_URL}/marketing.json`)
      .then(response => response.json())
      .then(data => setMarketingAnimation(data))
      .catch(err => console.error('Error loading animation:', err));
  }, []);

  // Set animation speed when loaded
  useEffect(() => {
    if (lottieRef.current) {
      lottieRef.current.setSpeed(0.25);
    }
  }, [marketingAnimation]);

  // Typewriter effect
  useEffect(() => {
    const currentWord = WORDS[currentWordIndex];
    const timeout = setTimeout(() => {
      if (!isDeleting) {
        // Typing
        if (displayText.length < currentWord.length) {
          setDisplayText(currentWord.substring(0, displayText.length + 1));
        } else {
          // Pause before deleting
          setTimeout(() => setIsDeleting(true), 2000);
        }
      } else {
        // Deleting
        if (displayText.length > 0) {
          setDisplayText(currentWord.substring(0, displayText.length - 1));
        } else {
          setIsDeleting(false);
          setCurrentWordIndex((prevIndex) => (prevIndex + 1) % WORDS.length);
        }
      }
    }, isDeleting ? 50 : 150);

    return () => clearTimeout(timeout);
  }, [displayText, isDeleting, currentWordIndex]);


  const handleCardClick = (cardData, event) => {
    const cardElement = cardRefs.current[cardData.id];
    const wasHovered = hoveredCard === cardData.id;
    if (cardElement) {
      const rect = cardElement.getBoundingClientRect();
      setCardPosition({
        top: wasHovered ? rect.top + 8 : rect.top,
        left: rect.left,
        width: rect.width,
        height: rect.height
      });
    }
    // Capture section position before it goes fixed
    const section = cardsSectionRef.current;
    if (section) {
      const sRect = section.getBoundingClientRect();
      setSectionRect({
        top: sRect.top,
        left: sRect.left,
        right: window.innerWidth - sRect.right,
        bottom: window.innerHeight - sRect.bottom
      });
    }
    setHoveredCard(null);
    setSelectedCard(cardData);
    setTimeout(() => setIsExpanded(true), 50);
  };

  const handleBack = () => {
    // Step 1: Fade out selected card and start hiding all cards
    setIsExpanded(false);
    setHoveredCard(null);
    setHoverDisabled(true);
    setShowCardsContent(false);

    // Step 2: After fade, remove selected card and start morph + mark as returning
    setTimeout(() => {
      setSelectedCard(null);
      setCardPosition(null);
      setSectionRect(null);
      setIsReturning(true); // This hides all cards with card-hidden class
    }, 400);

    // Step 3: Show cards with animation as morph nears completion (700ms morph starts at 400ms = completes at 1100ms)
    setTimeout(() => {
      setShowCardsContent(true); // This triggers card-animate-in
    }, 1000);

    // Step 4: Clean up states after all animations complete
    setTimeout(() => {
      setIsReturning(false);
      setHoveredCard(null);
      setTimeout(() => {
        setHoverDisabled(false);
      }, 100);
    }, 2500);
  };

  const handleContinueDemo = (e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    // Email not required for demo

    // Instant page change to workspace
    setIsTransitioning(true);
    setShowCardsContent(false);
    setSelectedCard(null);
    setIsExpanded(false);
    setCardPosition(null);
    setIsReturning(false);
    setShowWorkspace(true);
    setCurrentView('workspace');
  };

  const handleCreatePresentation = () => {
    setCurrentView('loading');
  };

  const handleLogoClick = () => {
    setShowWorkspace(true);
    setCurrentView('loading');
  };

  const handleLoadingComplete = () => {
    setCurrentView('results');
  };

  const handleCreateAnother = () => {
    setCurrentView('workspace');
  };

  const cardData = [
    {
      id: 0,
      label: 'Create a presentation',
      title: 'Client Proposal',
      subtitle: 'Custom proposal, same grind? Not anymore.'
    },
    {
      id: 1,
      label: 'Create a presentation',
      title: 'Sustainability Report',
      subtitle: 'Finally, a report you won\'t hate building.'
    },
    {
      id: 2,
      label: 'Create a presentation',
      title: 'Transformation Roadmap',
      subtitle: 'From whiteboard scribbles to boardroom ammo.'
    }
  ];

  const handleBackToOverview = () => {
    // Hard page change back to landing
    setPageTransition(true);

    setTimeout(() => {
      setShowWorkspace(false);
      setCurrentView('landing');
      setEmail('');
      setSelectedCard(null);
      setIsExpanded(false);
      setCardPosition(null);
      setIsTransitioning(false);
      setShowCardsContent(true);
      setIsReturning(false);

      requestAnimationFrame(() => {
        setPageTransition(false);
      });
    }, 150);
  };

  return (
    <>
      {pageTransition && (
        <div className="page-transition-blank" />
      )}
      {showWorkspace && currentView === 'workspace' && (
        <Workspace
          onBack={handleBackToOverview}
          onCreatePresentation={handleCreatePresentation}
          documentTitle={selectedCard?.title || 'Strategic Partnership Proposal'}
          isLoading={false}
          onLoadingComplete={handleLoadingComplete}
          onLogoClick={handleLogoClick}
        />
      )}
      {showWorkspace && currentView === 'loading' && (
        <FullScreenLoadingView
          documentTitle={selectedCard?.title || 'Strategic Partnership Proposal'}
          cardSubtitle={selectedCard?.subtitle || ''}
          onComplete={handleLoadingComplete}
          onBack={handleBackToOverview}
          onLogoClick={handleLogoClick}
        />
      )}
      {showWorkspace && currentView === 'results' && (
        <FullScreenResultsView
          documentTitle={selectedCard?.title || 'Strategic Partnership Proposal'}
          onCreateAnother={handleCreateAnother}
          onBackToOverview={handleBackToOverview}
          onLogoClick={handleLogoClick}
        />
      )}
      <div className={`templafy-container ${selectedCard ? 'card-selected' : ''} ${showWorkspace ? 'hidden' : ''}`}>
      <div className={`templafy-header ${isTransitioning ? 'transitioning-fade' : ''}`}>
        <img src="/templafy-logo.svg" alt="Templafy" className="logo" onClick={handleLogoClick} style={{ cursor: 'pointer' }} />
      </div>

      <div className={`templafy-content ${isTransitioning ? 'transitioning-fade' : ''}`}>
        <div className={`hero-section ${selectedCard ? 'fade-out' : ''}`} style={{ opacity: showCardsContent ? 1 : 0, transition: 'opacity 0.6s ease' }}>
          <h1 className="main-heading">PowerPoint got a <span className="blue">power-up.</span> Experience Templafy in action.</h1>
        </div>

        <div
          className={`cards-section ${selectedCard ? 'selection-active' : ''} ${isExpanded ? 'expanded' : ''} ${isTransitioning ? 'transitioning' : ''}`}
          ref={(el) => { cardsContainerRef.current = el; cardsSectionRef.current = el; }}
          style={selectedCard && sectionRect && !isExpanded ? {
            position: 'fixed',
            top: `${sectionRect.top}px`,
            left: `${sectionRect.left}px`,
            right: `${sectionRect.right}px`,
            bottom: `${sectionRect.bottom}px`,
            width: 'auto',
            flex: 'none'
          } : {}}
        >
          <div className="cards-section-inner">
            <h2 className={`cards-section-title ${selectedCard ? 'fade-out' : ''}`} style={{ opacity: showCardsContent ? 1 : 0, transition: 'opacity 0.6s ease 0.15s' }}>Let's build a PowerPoint right now. Pick a presentation:</h2>
            <div className="cards-container">
                {cardData.map((card, index) => {
                  let cardClass = 'card-wrapper';

                  // When clicking back from email capture, fade out the selected card
                  if (selectedCard && selectedCard.id === card.id && !isExpanded) {
                    cardClass += ' fade-out';
                  }

                  // Fade out unselected cards when a card is selected (keep them hidden during return transition)
                  if (selectedCard && selectedCard.id !== card.id && (isExpanded || !showCardsContent)) {
                    cardClass += ' fade-out';
                  }

                  // Selected card in expanded state
                  if (selectedCard && selectedCard.id === card.id && isExpanded) {
                    cardClass += ' selected';
                  }

                  // Hide ALL cards during return morph (regardless of which was selected)
                  if (isReturning && !showCardsContent) {
                    cardClass += ' card-hidden';
                  }

                  // Animate ALL cards in after return
                  if (isReturning && showCardsContent && !selectedCard && !isTransitioning) {
                    cardClass += ' card-animate-in';
                  }

                  return (
                  <div
                    key={card.id}
                    className={cardClass}
                    style={{
                      animationDelay: (isReturning && showCardsContent && !selectedCard && !isTransitioning) ? `${index * 0.15}s` : '0s'
                    }}
                  >
                    <div
                      ref={(el) => cardRefs.current[card.id] = el}
                      className={`card ${selectedCard && selectedCard.id === card.id ? 'expanded' : ''} ${card.id !== 0 ? 'card-disabled' : ''}`}
                      style={{
                        transform: `scale(${hoveredCard === card.id && !selectedCard && !isReturning && !hoverDisabled ? 1.03 : 1})`,
                        ...(selectedCard && selectedCard.id === card.id && cardPosition && !isExpanded ? {
                          position: 'fixed',
                          top: `${cardPosition.top}px`,
                          left: `${cardPosition.left}px`,
                          width: '320px',
                          height: '480px',
                          zIndex: 100,
                          transition: 'top 0.7s cubic-bezier(0.65, 0, 0.35, 1), left 0.7s cubic-bezier(0.65, 0, 0.35, 1), transform 0.7s cubic-bezier(0.65, 0, 0.35, 1)'
                        } : {}),
                        ...(selectedCard && selectedCard.id === card.id && isExpanded ? {
                          position: 'fixed',
                          top: 'calc(116px + (100vh - 116px - 40px) / 2)',
                          left: 'calc(50% - 450px)',
                          transform: 'translateY(-50%)',
                          width: '320px',
                          height: '480px',
                          zIndex: 100,
                          transition: 'top 0.7s cubic-bezier(0.65, 0, 0.35, 1), left 0.7s cubic-bezier(0.65, 0, 0.35, 1), transform 0.7s cubic-bezier(0.65, 0, 0.35, 1)'
                        } : {})
                      }}
                      onMouseEnter={() => card.id === 0 && !selectedCard && !isReturning && setHoveredCard(card.id)}
                      onMouseLeave={() => card.id === 0 && !selectedCard && !isReturning && setHoveredCard(null)}
                      onClick={(e) => card.id === 0 && !selectedCard && !isReturning && handleCardClick(card, e)}
                    >
                      {card.id === 0 ? (
                        <div className="card-label" style={(selectedCard && selectedCard.id === card.id) ? { opacity: 0, pointerEvents: 'none' } : {}}>
                          <img src="/powerpoint-icon.svg" alt="PowerPoint" className="card-label-icon" />
                          {card.label}
                        </div>
                      ) : (
                        <span className="card-coming-soon" style={(selectedCard && selectedCard.id === card.id) ? { opacity: 0, pointerEvents: 'none' } : { cursor: 'pointer' }} onClick={(e) => { e.stopPropagation(); setShowWorkspace(true); setCurrentView('workspace'); }}>Coming soon</span>
                      )}
                      <h3 className="card-title">{card.title}</h3>
                      {!(selectedCard && selectedCard.id === card.id) && (
                        <>
                          <p className="card-subtitle">{card.subtitle}</p>
                          {card.id === 0 && (
                            <button
                              className="card-get-started"
                              style={{ marginBottom: Math.max(0, btnBottomOffset - 24) }}
                              onClick={(e) => {
                                e.stopPropagation();
                                handleCardClick(card, e);
                              }}
                            >
                              Get Started
                            </button>
                          )}
                        </>
                      )}
                      {selectedCard && selectedCard.id === card.id && isExpanded && (
                        <div className="card-skeleton-lines">
                          {/* 3 lines */}
                          <div className="card-skeleton-line" style={{ width: '100%', animationDelay: '0s' }} />
                          <div className="card-skeleton-line" style={{ width: '85%', animationDelay: '0.1s' }} />
                          <div className="card-skeleton-line" style={{ width: '55%', animationDelay: '0.2s' }} />

                          {/* Image */}
                          <div className="card-skeleton-image" style={{ animationDelay: '0.35s' }} />

                          {/* 5 lines */}
                          <div className="card-skeleton-line" style={{ width: '95%', animationDelay: '0.55s' }} />
                          <div className="card-skeleton-line" style={{ width: '80%', animationDelay: '0.65s' }} />
                          <div className="card-skeleton-line" style={{ width: '92%', animationDelay: '0.75s' }} />
                          <div className="card-skeleton-line" style={{ width: '70%', animationDelay: '0.85s' }} />
                          <div className="card-skeleton-line" style={{ width: '45%', animationDelay: '0.95s' }} />

                          {/* 2 lines */}
                          <div className="card-skeleton-spacer" />
                          <div className="card-skeleton-line" style={{ width: '88%', animationDelay: '1.1s' }} />
                          <div className="card-skeleton-line" style={{ width: '60%', animationDelay: '1.2s' }} />
                        </div>
                      )}
                    </div>
                  </div>
                  );
                })}
            </div>
          </div>
        </div>
      </div>

      {selectedCard && isExpanded && !isTransitioning && (
        <>
          <button className="back-button" onClick={handleBack}>
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24">
              <title>xmark</title>
              <g fill="#232323" strokeLinecap="round" strokeLinejoin="round">
                <line x1="19" y1="19" x2="5" y2="5" fill="none" stroke="#232323" strokeWidth="2"></line>
                <line x1="19" y1="5" x2="5" y2="19" fill="none" stroke="#232323" strokeWidth="2"></line>
              </g>
            </svg>
            <span>Back to overview</span>
          </button>
          <div className="expanded-content">
            <h2 className="expanded-heading">Ready to see Templafy in action?</h2>
            <p className="expanded-subtext expanded-subtext-1">
              This is Templafy. If your organization had it, AI generated presentations would be beautiful, on-brand and compliant - automatically.
            </p>
            <p className="expanded-subtext expanded-subtext-2">
              Drop your work email to see it in action. No spam, ever.
            </p>
            <form className="expanded-form" onSubmit={handleContinueDemo}>
              <input
                type="text"
                placeholder="Your work email"
                className="expanded-input"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
              <button type="submit" className="expanded-button">Experience Templafy</button>
            </form>
            <p className="expanded-privacy">
              By giving us your email, you accept our <button type="button" className="expanded-link">privacy policy</button>
            </p>
          </div>
        </>
      )}
      </div>
    </>
  );
};

export default Templafy;
